// Light and Gravity Lab: four experiments ported from the widgets. Mixing (light adds,
// paint subtracts), Space-time (a mass dents the grid and a marble follows the dent), Black
// hole (light rays bend, some are captured) and True scale (how much real bodies bend space).
// Lesson, then do it; Explore after.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makeRay, stepRay, captureEdge, rsFor, type Ray } from './rays';

type Tab = 'mix' | 'well' | 'hole' | 'scale';
type Kind = 'white' | 'brown' | 'green' | 'choice' | 'dent' | 'curve' | 'edge' | 'predict' | 'order';

interface Step { tab: Tab; lesson: string; task: string; kind: Kind; choices?: string[]; answer?: number; why: string }

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

const STEPS: Record<1 | 2 | 3, Step[]> = {
  1: [
    { tab: 'mix', lesson: 'Light adds up. Red light plus green light plus blue light makes white, like the pixels in a phone screen.', task: 'Drag the three spotlights so all three overlap in one spot, with every light turned up.', kind: 'white', why: 'Red, green and blue light together make white. That is how screens show every color.' },
    { tab: 'mix', lesson: 'Paint is different. Each paint soaks up some light, so mixing paints makes darker colors, not white.', task: 'Switch to paint. Mix a brown: lots of yellow, some pink, and only a little light blue, all overlapping.', kind: 'brown', why: 'Paint takes light away. Mostly yellow and pink with a touch of blue leaves a muddy brown.' },
    { tab: 'well', lesson: 'Heavy things dent space, like a bowling ball on a trampoline. A heavier mass makes a deeper dent.', task: 'Slide Mass up to 80 or more to make a deep dent, then tap Check.', kind: 'dent', why: 'More mass, deeper dent. Planets roll along those dents: that is gravity.' },
  ],
  2: [
    { tab: 'mix', lesson: 'Lights add color. Paints take color away. So the same three colors behave in opposite ways.', task: 'Why do all the paints together make dark, but all the lights together make white?', kind: 'choice', choices: ['Light adds up; paint soaks light away', 'Paint is heavier than light', 'Lights are hotter', 'Paint dries darker'], answer: 0, why: 'Every paint absorbs some colors. Stack three and almost nothing bounces back: dark. Lights only add.' },
    { tab: 'well', lesson: 'A marble rolling near a dent curves toward it. Too little dent and it rolls straight; too much and it falls in.', task: 'Set the Mass so a launched marble curves around the dent and comes out the other side. Launch and watch.', kind: 'curve', why: 'The right amount of dent bends the path without catching the marble. Planets and comets do exactly this.' },
    { tab: 'hole', lesson: 'Near a black hole, even light bends. Rays aimed too close get captured. Rays aimed wide escape, bent.', task: 'Slide Aim down until you find the closest ray that STILL escapes. Fire it, then tap Check.', kind: 'edge', why: 'The edge between captured and escaped is the black hole\'s shadow. You just measured it.' },
  ],
  3: [
    { tab: 'hole', lesson: 'The shadow of a black hole depends on its mass. Think about what happens when the mass changes.', task: 'Predict: if the black hole\'s mass doubles, its shadow...', kind: 'predict', choices: ['Gets about twice as big', 'Stays the same', 'Gets smaller', 'Disappears'], answer: 0, why: 'The shadow grows with the mass: twice the mass, about twice the shadow. Test it with the Mass slider.' },
    { tab: 'scale', lesson: 'Every object dents space, even you. The dent depends on how much mass is squeezed into how little room.', task: 'Tap the four bodies in order from the SMALLEST dent to the BIGGEST: Earth, Sun, white dwarf, neutron star.', kind: 'order', why: 'Earth, then the Sun, then the white dwarf, then the neutron star. Squeezing mass into less room deepens the dent.' },
    { tab: 'mix', lesson: 'Paint mixing in reverse: pick the two paints whose leftover light is green.', task: 'Switch to paint and make green by overlapping just two colors.', kind: 'green', why: 'Light blue and yellow paint both let green light through, so green is what is left.' },
  ],
};

// ---- mixing data (from the widget) ----
interface Mode { n: string; bg: string; op: GlobalCompositeOperation; cols: [number, number, number][]; names: string[]; goals: { n: string; c: string; need: number[] }[]; tip: string }
const MODES: Mode[] = [
  { n: 'Light', bg: '#0b0b14', op: 'lighter', cols: [[255, 0, 0], [0, 255, 0], [0, 0, 255]], names: ['Red', 'Green', 'Blue'], goals: [{ n: 'Yellow', c: '#FFFF00', need: [0, 1] }, { n: 'Light blue', c: '#00FFFF', need: [1, 2] }, { n: 'Pink', c: '#FF00FF', need: [0, 2] }, { n: 'White', c: '#FFFFFF', need: [0, 1, 2] }], tip: 'Light adds up. All three together make white!' },
  { n: 'Paint', bg: '#ffffff', op: 'multiply', cols: [[0, 255, 255], [255, 0, 255], [255, 255, 0]], names: ['Light blue', 'Pink', 'Yellow'], goals: [{ n: 'Blue', c: '#0000FF', need: [0, 1] }, { n: 'Green', c: '#00FF00', need: [0, 2] }, { n: 'Red', c: '#FF0000', need: [1, 2] }, { n: 'Black', c: '#000000', need: [0, 1, 2] }], tip: 'Paint takes color away. All three together make dark!' },
];

// ---- true scale data (from the widget) ----
interface BodyDef { n: string; M: number; R: number | null; ms: string; rs: number; bh: boolean; q: number }
const BODIES: BodyDef[] = [
  { n: 'Earth', M: 3.003e-6, R: 6371, ms: '1 Earth' },
  { n: 'Sun', M: 1, R: 695700, ms: '1 Sun' },
  { n: 'White dwarf', M: 1.02, R: 5800, ms: '1.02 Suns' },
  { n: 'Neutron star', M: 1.4, R: 12, ms: '1.4 Suns' },
  { n: 'Black hole', M: 10, R: null, ms: '10 Suns' },
  { n: 'Sagittarius A*', M: 4.3e6, R: null, ms: '4.3 million Suns' },
].map(b => { const rs = 2.953 * b.M, bh = b.R === null; return { ...b, rs, bh, q: bh ? 1 : rs / (b.R as number) }; });
const C_KMS = 299792;

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const steps = STEPS[tier];
  let si = 0;
  let explore = false;
  let attempts = 0, correct = 0, missed = false;
  const t0 = performance.now();
  let tStep = performance.now();
  let tab: Tab = steps[0].tab;

  // ---- mixing state ----
  let mode = 0;
  let C: { x: number; y: number }[] = [];
  let amt = [1, 1, 1];
  const found: Record<string, boolean>[] = [{}, {}];
  let drag = -1, off = [0, 0];
  // ---- well state ----
  let M = 0.45, wellTilt = 32 * Math.PI / 180;
  let part: { x: number; y: number; vx: number; vy: number } | null = null;
  let trail: [number, number][] = [];
  let turned = 0, lastHeading = 0, launched = false, curveDone = false;
  // ---- hole state ----
  let massSlider = 55, aim = 40, holeTilt = 34 * Math.PI / 180;
  let rays: Ray[] = [];
  let predicted: number | null = null, massAtPredict = 55;
  // ---- scale state ----
  let sel = 4, exLog = 0, scaleTilt = 30 * Math.PI / 180;
  let order: number[] = [];

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const tabs = h('div.tabs', { style: { padding: '0 0 8px' } });
  const stage = h('div');
  const readout = h('div.row', { style: { justifyContent: 'space-between', fontWeight: '800', minHeight: '24px' } }, h('span', null, ''), h('span.muted.small', null, ''));
  const controls = h('div.stack');
  const choices = h('div.grid2');
  host.append(lesson, task, tabs, stage, readout, controls, choices);
  const S = makeSurface(stage, { aspect: 0.82, onResize: () => { if (!C.length) placeLights(); } });

  function renderTabs() {
    clear(tabs);
    for (const [id, label] of [['mix', 'Mixing'], ['well', 'Space-time'], ['hole', 'Black hole'], ['scale', 'True scale']] as [Tab, string][]) {
      const locked = !explore && steps[si] && steps[si].tab !== id;
      tabs.append(h('button.tab' + (tab === id ? '.on' : ''), { disabled: locked, style: { opacity: locked ? '.45' : '1', padding: '6px 10px' }, onclick: () => { tab = id; renderTabs(); renderControls(); } }, label));
    }
  }
  function slider(label: string, min: number, max: number, value: number, step: number, fmt: (v: number) => string, on: (v: number) => void): HTMLElement {
    const out = h('span', { style: { minWidth: '54px', textAlign: 'right', fontWeight: '800' } }, fmt(value));
    const inp = h('input', { type: 'range', min: String(min), max: String(max), step: String(step), value: String(value), 'aria-label': label }) as HTMLInputElement;
    inp.addEventListener('input', () => { const v = Number(inp.value); out.textContent = fmt(v); on(v); });
    return h('div.row', null, h('span.muted.small', { style: { minWidth: '84px' } }, label), inp, out);
  }
  function addChoices(st: Step) { st.choices!.forEach((c, i) => choices.append(h('button.choice', { style: { fontSize: '15px' }, onclick: () => answer(i) }, c))); }

  function renderControls() {
    clear(controls); clear(choices);
    const st = explore ? null : steps[si];
    if (tab === 'mix') {
      controls.append(h('div.grid2', null, ...MODES.map((m, i) => h('button.btn.small' + (mode === i ? '.primary' : ''), { onclick: () => { mode = i; amt = [1, 1, 1]; placeLights(); renderControls(); ctx.say(i ? 'Now try mixing paint. Is it the same?' : 'Drag the circles so they overlap.'); } }, `Mix ${m.n.toLowerCase()}`))));
      const m = MODES[mode];
      for (let i = 0; i < 3; i++) controls.append(slider(m.names[i], 0, 100, Math.round(amt[i] * 100), 1, v => `${v}%`, v => { amt[i] = v / 100; checkGoals(); }));
      const g = h('div.row.wrap', { style: { gap: '6px' } });
      m.goals.forEach((goal, k) => g.append(h('span.pill', { style: { background: found[mode][k] ? 'var(--good)' : '', color: found[mode][k] ? '#fff' : '', fontSize: '13px' } }, h('span', { style: { width: '12px', height: '12px', borderRadius: '50%', background: goal.c, border: '1px solid var(--line)', display: 'inline-block' } }), goal.n)));
      controls.append(g);
      controls.append(h('button.btn.small.ghost', { onclick: () => { amt = [1, 1, 1]; placeLights(); renderControls(); } }, 'Start over'));
      if (st && (st.kind === 'white' || st.kind === 'brown' || st.kind === 'green')) controls.append(h('button.btn.primary.block', { onclick: checkMix }, 'Check'));
      if (st && st.kind === 'choice') addChoices(st);
    } else if (tab === 'well') {
      controls.append(slider('Mass', 0, 100, Math.round(M * 100), 1, v => String(v), v => { M = v / 100; }));
      controls.append(slider('Tilt', 15, 60, Math.round(wellTilt * 180 / Math.PI), 1, v => `${v} deg`, v => { wellTilt = v * Math.PI / 180; }));
      controls.append(h('div.grid2', null,
        h('button.btn.small.primary', { onclick: launch }, 'Launch marble'),
        h('button.btn.small.ghost', { onclick: () => { part = null; trail = []; } }, 'Clear path'),
      ));
      if (st && st.kind === 'dent') controls.append(h('button.btn.primary.block', { onclick: () => { if (M >= 0.8) pass(st); else fail('Slide Mass up to 80 or more. Watch the grid dip deeper.'); } }, 'Check'));
    } else if (tab === 'hole') {
      controls.append(slider('Mass', 10, 100, massSlider, 1, v => String(v), v => { massSlider = v; }));
      controls.append(slider('Aim', 0, 90, aim, 1, v => `${(v / 100 / rsFor(massSlider)).toFixed(1)} rs`, v => { aim = v; }));
      controls.append(slider('Tilt', 15, 60, Math.round(holeTilt * 180 / Math.PI), 1, v => `${v} deg`, v => { holeTilt = v * Math.PI / 180; }));
      controls.append(h('div.grid2', null,
        h('button.btn.small.primary', { onclick: () => addRay(aim / 100) }, 'Fire one ray'),
        h('button.btn.small', { onclick: () => { for (let i = 0; i < 12; i++) addRay(-0.88 + 1.76 * i / 11); } }, 'Fire a fan'),
        h('button.btn.small.ghost', { onclick: () => { rays = []; } }, 'Clear rays'),
        st && st.kind === 'edge' ? h('button.btn.small.primary', { onclick: checkEdge }, 'Check') : h('span'),
      ));
      if (st && st.kind === 'predict') {
        if (predicted === null) addChoices(st);
        else controls.append(h('button.btn.primary.block', { onclick: checkPredict }, 'I doubled the mass: Check'));
      }
    } else {
      controls.append(slider('Exaggerate', 0, 5, exLog, 0.1, v => v <= 0.01 ? 'true scale' : `x${Math.round(Math.pow(10, v))}`, v => { exLog = v; }));
      controls.append(slider('Tilt', 15, 60, Math.round(scaleTilt * 180 / Math.PI), 1, v => `${v} deg`, v => { scaleTilt = v * Math.PI / 180; }));
      const rows = h('div.stack', { style: { gap: '4px' } });
      BODIES.forEach((b, i) => {
        const pct = Math.max(4, (Math.log10(b.q) + 10) * 10);
        const inOrder = order.indexOf(i);
        rows.append(h('button.btn.small', { style: { justifyContent: 'space-between', gap: '8px', borderColor: sel === i ? 'var(--accent)' : '', background: inOrder >= 0 ? 'var(--surface-2)' : '' }, onclick: () => pickBody(i) },
          h('span', { style: { minWidth: '92px', textAlign: 'left', fontSize: '13px' } }, inOrder >= 0 ? `${inOrder + 1}. ${b.n}` : b.n),
          h('span', { style: { flex: '1', height: '8px', borderRadius: '4px', background: 'var(--surface-2)', overflow: 'hidden' } }, h('span', { style: { display: 'block', height: '100%', width: `${pct}%`, borderRadius: '4px', background: b.bh ? '#7F77DD' : '#EF9F27' } })),
          h('span.muted', { style: { fontSize: '12px', minWidth: '86px', textAlign: 'right' } }, b.bh ? 'light speed' : `${Number((C_KMS * Math.sqrt(b.q)).toPrecision(3)).toLocaleString()} km/s`),
        ));
      });
      controls.append(h('p.muted.small', null, 'Escape speed at the surface. Tap a body to view its dent.'), rows);
      if (st && st.kind === 'order') controls.append(h('button.btn.small.ghost', { onclick: () => { order = []; renderControls(); } }, 'Start the order over'));
    }
  }

  // ---- mixing ----
  function R(): number { return Math.min(S.w, S.h) * 0.24; }
  function placeLights() { C = [{ x: S.w * 0.2, y: S.h * 0.25 }, { x: S.w * 0.8, y: S.h * 0.25 }, { x: S.w * 0.5, y: S.h * 0.78 }]; }
  function colOf(i: number): string {
    const m = MODES[mode], c = m.cols[i], a = amt[i];
    if (mode === 0) return `rgb(${Math.round(c[0] * a)},${Math.round(c[1] * a)},${Math.round(c[2] * a)})`;
    return `rgb(${Math.round(255 - (255 - c[0]) * a)},${Math.round(255 - (255 - c[1]) * a)},${Math.round(255 - (255 - c[2]) * a)})`;
  }
  function ov(a: number, b: number): boolean { const r = R(); return Math.hypot(C[a].x - C[b].x, C[a].y - C[b].y) < r * 1.8; }
  function tripleOverlap(): boolean { const r = R(), cx = (C[0].x + C[1].x + C[2].x) / 3, cy = (C[0].y + C[1].y + C[2].y) / 3; return ov(0, 1) && ov(1, 2) && ov(0, 2) && C.every(c => Math.hypot(c.x - cx, c.y - cy) < r * 0.95); }
  function checkGoals() {
    const m = MODES[mode];
    let newOne: string | null = null;
    m.goals.forEach((g, k) => {
      const on = g.need.every(i => amt[i] >= 0.6);
      const ok = on && (g.need.length === 2 ? ov(g.need[0], g.need[1]) : tripleOverlap());
      if (ok && !found[mode][k]) { found[mode][k] = true; newOne = g.n; }
    });
    if (newOne) { const all = m.goals.every((_, k) => found[mode][k]); ctx.say(all ? `You made them all! ${m.tip}` : `You made ${(newOne as string).toLowerCase()}!`); ctx.sound('pop'); renderControls(); }
  }
  /** the paint color where all three overlap, each channel 0..1 (multiply model) */
  function paintMix(): [number, number, number] { return [1 - amt[0], 1 - amt[1], 1 - amt[2]]; }
  function checkMix() {
    const st = steps[si];
    if (st.kind === 'white') {
      if (mode !== 0) { fail('Switch to Mix light first.'); return; }
      if (!amt.every(a => a >= 0.6)) { fail('Turn every light up to at least 60%.'); return; }
      if (!tripleOverlap()) { fail('Drag all three spotlights so they pile up in one spot.'); return; }
      ctx.charm('charm.white'); pass(st);
    } else if (st.kind === 'brown') {
      if (mode !== 1) { fail('Switch to Mix paint first.'); return; }
      if (!tripleOverlap()) { fail('Drag all three paint circles so they overlap in one spot.'); return; }
      const [r, g, b] = paintMix();
      if (r > g && g > b && b < 0.35 && r > 0.25 && r < 0.9) pass(st);
      else fail(`Right now the middle is ${describeRGB(r, g, b)}. Brown needs lots of yellow, some pink, only a little light blue. Lower the light blue slider most.`);
    } else if (st.kind === 'green') {
      if (mode !== 1) { fail('Switch to Mix paint first.'); return; }
      if (amt[0] >= 0.6 && amt[2] >= 0.6 && ov(0, 2) && (amt[1] < 0.3 || !ov(0, 1) || !ov(1, 2))) pass(st);
      else fail('Green comes from light blue plus yellow. Overlap those two and keep pink out of it.');
    }
  }
  function describeRGB(r: number, g: number, b: number): string {
    if (r < 0.15 && g < 0.15 && b < 0.15) return 'almost black';
    if (b > r && b > g) return 'bluish';
    if (g > r && g > b) return 'greenish';
    if (r > 0.6 && g < 0.3) return 'reddish';
    return 'muddy';
  }

  // ---- space-time well ----
  const A2 = 0.05, K = 0.13;
  function projWell(x: number, y: number): [number, number] { const d = K * M / Math.sqrt(x * x + y * y + A2); const Sc = S.w * 0.46; return [S.w / 2 + x * Sc, S.h * 0.42 + y * Sc * Math.sin(wellTilt) + d * Sc * Math.cos(wellTilt)]; }
  function launch() { part = { x: -0.75, y: 0, vx: 0, vy: -0.55 }; trail = []; turned = 0; lastHeading = Math.atan2(-0.55, 0); launched = true; curveDone = false; }
  function stepWell(dt: number) {
    if (!part) return;
    const r2 = part.x * part.x + part.y * part.y + A2;
    const f = -0.6 * M / Math.pow(r2, 1.5);
    part.vx += f * part.x * dt; part.vy += f * part.y * dt; part.x += part.vx * dt; part.y += part.vy * dt;
    const hd = Math.atan2(part.vy, part.vx);
    let d = hd - lastHeading; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
    turned += Math.abs(d); lastHeading = hd;
    if (part.x * part.x + part.y * part.y > 2.6) {
      part = null;
      const st = explore ? null : steps[si];
      const deg = turned * 180 / Math.PI;
      if (st && st.kind === 'curve' && !curveDone) {
        if (deg >= 45 && deg <= 330) { curveDone = true; pass(st); }
        else if (deg < 45) fail(`It only bent ${Math.round(deg)} degrees. More mass makes a deeper dent and a bigger curve.`);
        else fail('It spun around too much. A little less mass.');
      }
    }
  }

  // ---- black hole ----
  function rs(): number { return rsFor(massSlider); }
  function depth(r: number): number { const s = rs(); const zc = 2 * Math.sqrt(s * (1.5 - s)); return 0.55 * (zc - 2 * Math.sqrt(s * Math.max(r - s, 0))); }
  function projHole(x: number, y: number): [number, number] { const r = Math.sqrt(x * x + y * y), Sc = S.w * 0.46; return [S.w / 2 + x * Sc, S.h * 0.36 + y * Sc * Math.sin(holeTilt) + depth(r) * Sc * Math.cos(holeTilt)]; }
  function addRay(b: number) { if (rays.length > 40) rays.shift(); rays.push(makeRay(b)); ctx.sound('tick'); }
  function checkEdge() {
    const st = steps[si];
    const mine = rays.filter(r => r.st === 2 && r.b >= 0);
    if (!mine.length) { fail('Fire a ray that escapes first (green). Then slide Aim lower and try again.'); return; }
    const edge = captureEdge(rs());
    const closest = Math.min(...mine.map(r => Math.abs(r.b)));
    if (closest <= edge * 1.2) { ctx.charm('charm.escape'); pass(st); }
    else fail(`Your closest escaping ray was aimed at ${(closest / rs()).toFixed(1)} rs. Rays can get closer than that and still escape. Slide Aim lower.`);
  }
  function checkPredict() {
    const st = steps[si];
    if (massSlider < massAtPredict * 1.8) { fail(`Double the mass: slide it to about ${Math.min(100, Math.round(massAtPredict * 2))}.`); return; }
    if (predicted === st.answer) pass(st);
    else { ctx.say('Look at the dark circle: it grew with the mass. The shadow gets bigger, about double.'); pass(st); }
  }

  // ---- true scale ----
  function body(): BodyDef { return BODIES[sel]; }
  function zOf(p: number): number {
    const b = body(), q = b.q;
    if (b.bh) return 2 * Math.sqrt(Math.max(p - 1, 0));
    if (p >= 1) return 2 * Math.sqrt(q * (p - q));
    const rc2 = 1 / q;
    return 2 * Math.sqrt(q * (1 - q)) - (1 - p * p) / (Math.sqrt(rc2 - p * p) + Math.sqrt(rc2 - 1));
  }
  function exFactor(): number { const b = body(); const drop = b.bh ? zOf(4) : zOf(4) - zOf(0); const want = Math.pow(10, exLog), cap = 1.1 / (drop / 4); return Math.min(want, cap); }
  function dOf(p: number): number { return exFactor() * (zOf(4) - zOf(p)) / 4; }
  function projScale(x: number, y: number): [number, number] { const Sc = S.w * 0.46; return [S.w / 2 + x * Sc, S.h * 0.3 + y * Sc * Math.sin(scaleTilt) + dOf(4 * Math.sqrt(x * x + y * y)) * Sc * Math.cos(scaleTilt)]; }
  function pickBody(i: number) {
    sel = i;
    const st = explore ? null : steps[si];
    if (st && st.kind === 'order') {
      if (i > 3) { ctx.say('Just the first four for this one: Earth, Sun, white dwarf, neutron star.'); renderControls(); return; }
      if (order.includes(i)) { renderControls(); return; }
      order.push(i);
      const want = [0, 1, 2, 3];
      if (order[order.length - 1] !== want[order.length - 1]) { order = []; fail('Not that one next. Think: which of the remaining squeezes its mass into the least room?'); renderControls(); return; }
      if (order.length === 4) pass(st);
    }
    renderControls();
  }

  function fmtKm(v: number): string { if (v < 1) return `${Math.round(v * 1000)} m`; if (v < 100) return `${v.toFixed(1)} km`; if (v < 1e6) return `${Number(v.toPrecision(3)).toLocaleString()} km`; return `${Number((v / 1e6).toPrecision(3))} million km`; }

  // ---- steps ----
  function showStep() {
    const st = steps[si];
    (lesson.firstElementChild as HTMLElement).textContent = st.lesson;
    task.textContent = `Step ${si + 1} of ${steps.length}: ${st.task}`;
    tab = st.tab; missed = false; tStep = performance.now(); predicted = null; order = [];
    if (st.kind === 'white') { mode = 0; amt = [1, 1, 1]; placeLights(); }
    if (st.kind === 'brown' || st.kind === 'green') { mode = 1; amt = [1, 1, 1]; placeLights(); }
    if (st.kind === 'dent') M = 0.2;
    if (st.kind === 'curve') { M = 0.2; part = null; trail = []; }
    if (st.kind === 'edge') { rays = []; aim = 85; }
    if (st.kind === 'predict') { massSlider = 40; massAtPredict = 40; rays = []; for (let i = 0; i < 12; i++) addRay(-0.88 + 1.76 * i / 11); }
    renderTabs(); renderControls();
    ctx.speak(st.task);
  }
  function pass(st: Step) {
    if (!missed) { attempts++; correct++; }
    ctx.report({ skill: st.tab === 'mix' ? 'physics' : 'space', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `lgl:${tier}:${si}` });
    ctx.sound('ding'); ctx.say(st.why);
    si++;
    if (si >= steps.length) {
      explore = true;
      task.textContent = 'Round complete. All four experiments are open: explore!';
      renderTabs(); renderControls();
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1600);
      return;
    }
    setTimeout(showStep, 1800);
  }
  function fail(hint: string) {
    const st = steps[si];
    if (!missed) { attempts++; missed = true; ctx.report({ skill: st.tab === 'mix' ? 'physics' : 'space', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `lgl:${tier}:${si}` }); }
    ctx.sound('buzz'); ctx.say(hint);
  }
  function answer(i: number) {
    const st = steps[si];
    if (st.kind === 'predict') {
      predicted = i; massAtPredict = massSlider;
      if (i !== st.answer) fail('Not sure? Try it: slide the Mass up to double and watch the dark circle.');
      else ctx.say('Now test it: slide Mass to double, fire a fan, and tap Check.');
      renderControls();
      return;
    }
    if (i === st.answer) pass(st);
    else fail('Remember: light ADDS, paint TAKES AWAY. Which answer says that?');
  }

  // ---- pointer ----
  const unPointer = attachPointer(S.canvas, {
    down(p) {
      if (tab !== 'mix') return;
      const r = R(); let best = -1, bd = 1e9;
      for (let i = 0; i < 3; i++) { const d = Math.hypot(p.x - C[i].x, p.y - C[i].y); if (d < r && d < bd) { bd = d; best = i; } }
      if (best >= 0) { drag = best; off = [C[best].x - p.x, C[best].y - p.y]; }
    },
    move(p) { if (tab !== 'mix' || drag < 0) return; C[drag].x = Math.max(0, Math.min(S.w, p.x + off[0])); C[drag].y = Math.max(0, Math.min(S.h, p.y + off[1])); checkGoals(); },
    up() { drag = -1; },
  });

  function update() {
    for (let i = 0; i < 3; i++) stepWell(0.006);
    if (part) { trail.push([part.x, part.y]); if (trail.length > 700) trail.shift(); }
    const s = rs();
    for (const r of rays) { if (r.st) continue; for (let i = 0; i < 10 && !r.st; i++) stepRay(r, s); if ((r.st as number) !== 1) r.tr.push([r.x, r.y]); }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    if (tab === 'mix') drawMix(c, W, H);
    else if (tab === 'well') drawWell(c, W, H);
    else if (tab === 'hole') drawHole(c, W, H);
    else drawScale(c, W, H);
  }
  function drawMix(c: CanvasRenderingContext2D, W: number, H: number) {
    const m = MODES[mode], r = R();
    if (!C.length) placeLights();
    c.globalCompositeOperation = 'source-over'; c.fillStyle = m.bg; c.fillRect(0, 0, W, H);
    c.globalCompositeOperation = m.op;
    for (let i = 0; i < 3; i++) { c.fillStyle = colOf(i); c.beginPath(); c.arc(C[i].x, C[i].y, r, 0, Math.PI * 2); c.fill(); }
    c.globalCompositeOperation = 'source-over';
    c.lineWidth = 1.5; c.setLineDash([4, 4]); c.strokeStyle = mode === 0 ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)';
    for (let i = 0; i < 3; i++) { c.beginPath(); c.arc(C[i].x, C[i].y, r, 0, Math.PI * 2); c.stroke(); }
    c.setLineDash([]);
    (readout.children[0] as HTMLElement).textContent = `${m.n}: drag the circles`;
    (readout.children[1] as HTMLElement).textContent = `${MODES[mode].goals.filter((_, k) => found[mode][k]).length} of 4 colors made`;
  }
  function drawWell(c: CanvasRenderingContext2D, W: number, H: number) {
    c.fillStyle = '#F1EFE8'; c.fillRect(0, 0, W, H);
    const N = 18, SEG = 36;
    c.strokeStyle = '#888780'; c.lineWidth = 0.75; c.globalAlpha = 0.7;
    for (let i = 0; i <= N; i++) {
      const cc = -1 + 2 * i / N;
      c.beginPath(); for (let j = 0; j <= SEG; j++) { const tt = -1 + 2 * j / SEG; const q = projWell(tt, cc); j ? c.lineTo(q[0], q[1]) : c.moveTo(q[0], q[1]); } c.stroke();
      c.beginPath(); for (let j = 0; j <= SEG; j++) { const tt = -1 + 2 * j / SEG; const q = projWell(cc, tt); j ? c.lineTo(q[0], q[1]) : c.moveTo(q[0], q[1]); } c.stroke();
    }
    c.globalAlpha = 1;
    if (M > 0.005) { const q = projWell(0, 0); c.fillStyle = '#EF9F27'; c.beginPath(); c.arc(q[0], q[1] - 2, 5 + M * 11, 0, Math.PI * 2); c.fill(); }
    if (trail.length > 1) { c.strokeStyle = '#1D9E75'; c.lineWidth = 2; c.beginPath(); trail.forEach((p, i) => { const q = projWell(p[0], p[1]); i ? c.lineTo(q[0], q[1]) : c.moveTo(q[0], q[1]); }); c.stroke(); }
    if (part) { const q = projWell(part.x, part.y); c.fillStyle = '#1D9E75'; c.beginPath(); c.arc(q[0], q[1], 5, 0, Math.PI * 2); c.fill(); }
    (readout.children[0] as HTMLElement).textContent = `Mass ${Math.round(M * 100)}`;
    (readout.children[1] as HTMLElement).textContent = launched ? `Path bent ${Math.round(turned * 180 / Math.PI)} degrees` : 'Launch a marble';
  }
  function drawHole(c: CanvasRenderingContext2D, W: number, H: number) {
    c.fillStyle = '#F1EFE8'; c.fillRect(0, 0, W, H);
    const N = 18, SEG = 60, s = rs();
    c.strokeStyle = '#888780'; c.lineWidth = 0.75; c.globalAlpha = 0.7;
    const gridLine = (fn: (t: number) => [number, number]) => { let pen = false; c.beginPath(); for (let j = 0; j <= SEG; j++) { const tt = -1 + 2 * j / SEG, xy = fn(tt); if (xy[0] * xy[0] + xy[1] * xy[1] < s * s) { pen = false; continue; } const q = projHole(xy[0], xy[1]); if (pen) c.lineTo(q[0], q[1]); else { c.moveTo(q[0], q[1]); pen = true; } } c.stroke(); };
    for (let i = 0; i <= N; i++) { const cc = -1 + 2 * i / N; gridLine(t => [t, cc]); gridLine(t => [cc, t]); }
    c.globalAlpha = 1; c.lineWidth = 1.5;
    const ring = (r: number, style: string, dash: number[], fill: string | null) => { const Sc = W * 0.46, q = projHole(r, 0); c.beginPath(); c.ellipse(W / 2, q[1], r * Sc, r * Sc * Math.sin(holeTilt), 0, 0, Math.PI * 2); if (fill) { c.fillStyle = fill; c.fill(); } c.setLineDash(dash); c.strokeStyle = style; c.stroke(); c.setLineDash([]); };
    ring(s, '#888780', [], '#0b0b0b');
    ring(1.5 * s, '#7F77DD', [5, 4], null);
    let cap = 0, esc = 0;
    for (const r of rays) {
      if (r.st === 1) cap++; if (r.st === 2) esc++;
      c.strokeStyle = r.st === 1 ? '#D85A30' : r.st === 2 ? '#1D9E75' : '#EF9F27'; c.lineWidth = 1.75; c.beginPath();
      r.tr.forEach((p, i) => { const q = projHole(p[0], p[1]); i ? c.lineTo(q[0], q[1]) : c.moveTo(q[0], q[1]); }); c.stroke();
      if (r.st === 0) { const q = projHole(r.x, r.y); c.fillStyle = '#EF9F27'; c.beginPath(); c.arc(q[0], q[1], 4, 0, Math.PI * 2); c.fill(); }
    }
    (readout.children[0] as HTMLElement).textContent = `Captured ${cap} · Escaped ${esc}`;
    (readout.children[1] as HTMLElement).textContent = `Shadow edge ${(captureEdgeCached() / s).toFixed(1)} rs`;
  }
  let edgeFor = -1, edgeVal = 0;
  function captureEdgeCached(): number { if (edgeFor !== massSlider) { edgeFor = massSlider; edgeVal = captureEdge(rs()); } return edgeVal; }
  function drawScale(c: CanvasRenderingContext2D, W: number, H: number) {
    c.fillStyle = '#F1EFE8'; c.fillRect(0, 0, W, H);
    const b = body(), N = 20, SEG = 64;
    c.strokeStyle = '#888780'; c.lineWidth = 0.75; c.globalAlpha = 0.7;
    const gridLine = (fn: (t: number) => [number, number]) => { let pen = false; c.beginPath(); for (let j = 0; j <= SEG; j++) { const tt = -1 + 2 * j / SEG, xy = fn(tt); if (b.bh && xy[0] * xy[0] + xy[1] * xy[1] < 0.0625) { pen = false; continue; } const q = projScale(xy[0], xy[1]); if (pen) c.lineTo(q[0], q[1]); else { c.moveTo(q[0], q[1]); pen = true; } } c.stroke(); };
    for (let i = 0; i <= N; i++) { const cc = -1 + 2 * i / N; gridLine(t => [t, cc]); gridLine(t => [cc, t]); }
    c.globalAlpha = 1; c.lineWidth = 1.5;
    const ring = (p: number, stroke: string, dash: number[], fill: string | null) => { const Sc = W * 0.46, yy = S.h * 0.3 + dOf(p) * Sc * Math.cos(scaleTilt); c.beginPath(); c.ellipse(W / 2, yy, p / 4 * Sc, p / 4 * Sc * Math.sin(scaleTilt), 0, 0, Math.PI * 2); if (fill) { c.fillStyle = fill; c.fill(); } c.setLineDash(dash); c.strokeStyle = stroke; c.stroke(); c.setLineDash([]); };
    if (b.bh) { ring(1, '#888780', [], '#0b0b0b'); ring(1.5, '#7F77DD', [5, 4], null); } else ring(1, '#EF9F27', [], 'rgba(239,159,39,0.22)');
    const Rr = b.bh ? b.rs : (b.R as number);
    const drop = b.bh ? zOf(4) : zOf(4) - zOf(0);
    c.fillStyle = '#2a2a2e'; c.font = '700 12px Nunito, sans-serif'; c.textAlign = 'left';
    c.fillText(`${b.n}: ${b.ms}`, 8, H - 40);
    c.fillText(`${b.bh ? 'Horizon' : 'Radius'} ${fmtKm(Rr)} · true dent ${fmtKm(drop * Rr)} (${Number((drop * 100).toPrecision(2))}% of radius)`, 8, H - 22);
    (readout.children[0] as HTMLElement).textContent = b.n;
    (readout.children[1] as HTMLElement).textContent = exFactor() <= 1.0001 ? 'true scale' : `x${Math.round(exFactor())} exaggerated`;
  }

  const loop = makeLoop(update, draw);
  loop.start();
  showStep();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.light-gravity-lab',
  title: 'Light and Gravity Lab',
  blurb: 'Mix light and paint, dent space with mass, bend light around a black hole.',
  category: 'science',
  skills: ['physics', 'space'],
  levelRange: [1, 10],
  unlock: { stars: 15 },
  mount,
};
export default mod;
